import React, { useEffect, useState } from "react";
import API from "../services/api";
import { Container, Table, Button, Alert, Badge, Card } from "react-bootstrap";
import logo from "../assets/logo.png";
import "./CertificatePage.css";
import "./SupplierCertificatePage.css";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");

const getStatus = (status) => {
  if (status === "generated") return { label: "Verified", tone: "success" };
  if (status === "rejected") return { label: "Rejected", tone: "danger" };
  return { label: "Pending", tone: "warning" };
};

const AdminSupplierCertificatePage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [message, setMessage] = useState("");

  const fetchSuppliers = async () => {
    try {
      const res = await API.get("/admin/suppliers");
      const list = Array.isArray(res.data) ? res.data : (res.data?.suppliers || []);
      const approved = list.filter((s) => s.approved === true);
      setSuppliers(approved);
      setMessage("");
    } catch (err) {
      setMessage("Failed to fetch suppliers");
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const openCertificate = (supplier, autoPrint = false) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const supplierName = supplier.profile?.supplierName || supplier.fullName || "Supplier";
    const companyName = supplier.profile?.companyName || supplier.fullName || "Supplier";
    const supplierCode = supplier.profile?.supplierCode || "N/A";
    const gst = supplier.profile?.gstNumber || "N/A";
    const contact = supplier.profile?.contactMobile || "N/A";
    const verificationCode = `${String(supplierName).split(/\s+/)[0]?.toUpperCase() || "SUPPLIER"}-${String(contact).replace(/\D/g, "").slice(0, 5) || "00000"}-AUTH`;
    const issuedAt = new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });

    w.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>Supplier Authorization Certificate</title>
          <link href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,200..800;1,6..72,200..800&family=Noto+Sans:wght@400;500;700&display=swap" rel="stylesheet" />
          <style>
            :root {
              --primary: #ef4444;
              --bg: #f9f2f2;
              --parchment: #fff8f8;
              --border-red: #991b1b;
              --text: #111827;
              --muted: #6b7280;
            }
            * { box-sizing: border-box; }
            body { margin: 0; background: var(--bg); font-family: "Newsreader", serif; color: var(--text); }
            .page { min-height: 100vh; display: flex; flex-direction: column; }
            main { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 40px 16px 64px; }
            .print-area {
              width: 100%;
              max-width: 900px;
              background: var(--parchment);
              position: relative;
              padding: 48px;
              border: 12px solid var(--border-red);
              box-shadow: 0 30px 60px rgba(0,0,0,0.15);
            }
            .watermark { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0.03; pointer-events: none; }
            .content { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 20px; }
            .brand-row {
              display: flex;
              align-items: center;
              gap: 10px;
              font-family: "Noto Sans", sans-serif;
              font-weight: 700;
              letter-spacing: 0.2em;
              color: var(--border-red);
              text-transform: uppercase;
              font-size: 14px;
            }
            .brand-row img { height: 48px; }
            .title { font-size: 46px; color: var(--border-red); font-style: italic; margin: 10px 0 4px; }
            .divider { width: 120px; height: 4px; background: rgba(239, 68, 68, 0.2); margin: 6px auto 0; }
            .details {
              width: 100%;
              display: grid;
              grid-template-columns: repeat(4, minmax(0, 1fr));
              gap: 16px;
              border-top: 1px solid rgba(239, 68, 68, 0.15);
              border-bottom: 1px solid rgba(239, 68, 68, 0.15);
              padding: 18px 0;
            }
            .detail-label { font-family: "Noto Sans", sans-serif; font-size: 10px; color: var(--primary); letter-spacing: 0.2em; text-transform: uppercase; font-weight: 700; }
            .detail-value { font-family: "Noto Sans", sans-serif; font-size: 13px; font-weight: 700; }
            .footer { width: 100%; display: flex; justify-content: space-between; align-items: flex-end; margin-top: 18px; }
            .signature { text-align: right; }
            .signature .name { font-family: "Noto Sans", sans-serif; font-weight: 700; }
            .signature .line { height: 1px; width: 160px; background: rgba(239, 68, 68, 0.35); margin: 8px 0 6px auto; }
          </style>
        </head>
        <body>
          <div class="page">
            <main>
              <div class="print-area">
                <div class="watermark"><div style="font-size: 120px; font-weight: 800; color: #991b1b;">CERTIFIED</div></div>
                <div class="content">
                  <div class="brand-row"><img src="${logo}" alt="Logo" /> Lotus Rice Mill Pvt. Ltd.</div>
                  <div>Supplier Authorization</div>
                  <div class="title">Certificate</div>
                  <div class="divider"></div>
                  <div>
                    <p>This is to officially certify that</p>
                    <h2>${supplierName}</h2>
                    <p class="desc">${companyName} has been duly vetted and is hereby recognized as an authorized supplier partner for Lotus Rice Mill Pvt. Ltd.</p>
                  </div>
                  <div class="details">
                    <div><div class="detail-label">Supplier Code</div><div class="detail-value">${supplierCode}</div></div>
                    <div><div class="detail-label">GSTIN</div><div class="detail-value">${gst}</div></div>
                    <div><div class="detail-label">Verification</div><div class="detail-value">${verificationCode}</div></div>
                    <div><div class="detail-label">Contact</div><div class="detail-value">${contact}</div></div>
                  </div>
                  <div class="footer">
                    <div>
                      <div class="detail-label">Date of Issue</div>
                      <div class="detail-value">${issuedAt}</div>
                    </div>
                    <div class="signature">
                      <div class="name">Anil Saxena</div>
                      <div class="detail-label">Authorized Signatory</div>
                      <div class="line"></div>
                      <div class="detail-label">Lotus Rice Mill Pvt. Ltd.</div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </body>
      </html>
    `);
    w.document.close();
    if (autoPrint) {
      setTimeout(() => {
        w.focus();
        w.print();
      }, 300);
    }
  };

  const handleGenerate = async (supplier) => {
    try {
      await API.put(`/admin/suppliers/${supplier._id}/generate-certificate`);
      setMessage("Certificate generated");
      fetchSuppliers();
      openCertificate(supplier, false);
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setMessage(apiError || err?.response?.data?.message || "Failed to generate certificate");
    }
  };

  const handleView = (supplier) => openCertificate(supplier, false);
  const handleDownload = (supplier) => openCertificate(supplier, true);

  const handleReject = async (supplierId) => {
    try {
      await API.put(`/admin/suppliers/${supplierId}/reject-certificate`);
      fetchSuppliers();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setMessage(apiError || err?.response?.data?.message || "Failed to reject certificate");
    }
  };

  return (
    <Container className="mt-4 certificate-page supplier-certificate-page">
      <div className="certificate-header">
        <div>
          <h4 className="mb-1">Eligible Suppliers for Certificate</h4>
          <p className="certificate-subtitle">Manage, verify, and issue supplier authorization certificates.</p>
        </div>
        <div className="certificate-actions">
          <Button variant="outline-secondary" size="sm" onClick={fetchSuppliers}>Refresh Data</Button>
        </div>
      </div>
      {message && <Alert variant="danger">{message}</Alert>}

      <div className="d-none d-md-block">
        <div className="certificate-table-card">
          <div className="certificate-table-header">
            <h5>Eligible Suppliers</h5>
            <div className="certificate-table-tools">
              <span className="material-symbols-outlined">filter_list</span>
              <span className="material-symbols-outlined">more_vert</span>
            </div>
          </div>
          <Table hover responsive className="certificate-table">
            <thead>
              <tr>
                <th>Supplier Name</th>
                <th>Contact Info</th>
                <th>Current Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((supplier) => {
                const status = getStatus(supplier.certificateStatus);
                const displayName = supplier.profile?.supplierName || supplier.fullName || "Supplier";
                return (
                  <tr key={supplier._id}>
                    <td>
                      <div className="certificate-vendor">
                        <div className="certificate-avatar">{getInitials(displayName)}</div>
                        <span className="certificate-name">{displayName}</span>
                      </div>
                    </td>
                    <td>
                      <div className="certificate-contact">
                        <span className="certificate-phone">{supplier.mobile || supplier.profile?.contactMobile || "—"}</span>
                        <span className="certificate-email">{supplier.email || "—"}</span>
                      </div>
                    </td>
                    <td>
                      <Badge className={`certificate-status ${status.tone}`}>
                        <span className="status-dot" />
                        {status.label}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <div className="certificate-row-actions">
                        <Button
                          size="sm"
                          className="certificate-btn success"
                          onClick={() => handleGenerate(supplier)}
                          disabled={supplier.certificateStatus === "generated"}
                        >
                          Generate Certificate
                        </Button>
                        <Button
                          size="sm"
                          className="certificate-btn secondary"
                          onClick={() => handleView(supplier)}
                          disabled={supplier.certificateStatus !== "generated"}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          className="certificate-btn dark"
                          onClick={() => handleDownload(supplier)}
                          disabled={supplier.certificateStatus !== "generated"}
                        >
                          Download PDF
                        </Button>
                        <Button
                          size="sm"
                          className="certificate-btn danger"
                          onClick={() => handleReject(supplier._id)}
                          disabled={supplier.certificateStatus === "rejected"}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="d-md-none">
        {suppliers.map((supplier) => {
          const displayName = supplier.profile?.supplierName || supplier.fullName || "Supplier";
          const status = getStatus(supplier.certificateStatus);
          return (
            <Card key={supplier._id} className="mb-3 shadow-sm certificate-card">
              <Card.Body>
                <div className="certificate-vendor">
                  <div className="certificate-avatar">{getInitials(displayName)}</div>
                  <div>
                    <h5 className="mb-1">{displayName}</h5>
                    <p className="mb-1 text-muted">{supplier.mobile || supplier.profile?.contactMobile || "—"}</p>
                    <p className="mb-1">{supplier.email || "—"}</p>
                  </div>
                </div>
                <Badge className={`certificate-status ${status.tone} mb-2`}>
                  <span className="status-dot" />
                  {status.label}
                </Badge>
                <div className="d-flex gap-2 mt-2">
                  <Button
                    size="sm"
                    className="certificate-btn success"
                    onClick={() => handleGenerate(supplier)}
                    disabled={supplier.certificateStatus === "generated"}
                  >
                    Generate Certificate
                  </Button>
                  <Button
                    size="sm"
                    className="certificate-btn secondary"
                    onClick={() => handleView(supplier)}
                    disabled={supplier.certificateStatus !== "generated"}
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    className="certificate-btn dark"
                    onClick={() => handleDownload(supplier)}
                    disabled={supplier.certificateStatus !== "generated"}
                  >
                    PDF
                  </Button>
                  <Button
                    size="sm"
                    className="certificate-btn danger"
                    onClick={() => handleReject(supplier._id)}
                    disabled={supplier.certificateStatus === "rejected"}
                  >
                    Reject
                  </Button>
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    </Container>
  );
};

export default AdminSupplierCertificatePage;
